package com.fantasytenis.shared.data.tennis.repository

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.data.tennis.api.TennisApi
import com.fantasytenis.shared.data.tennis.dto.*
import com.fantasytenis.shared.domain.tennis.entity.*
import com.fantasytenis.shared.domain.scoring.entity.MatchStatisticsEntity

/**
 * Tennis Repository Interface.
 *
 * Defines the contract for tennis data operations.
 */
interface TennisRepository {
    suspend fun getPlayers(slateId: String? = null): Result<List<TennisPlayerEntity>>
    suspend fun getPlayer(playerId: String): Result<TennisPlayerEntity>
    suspend fun getLiveMatches(): Result<List<MatchEntity>>
    suspend fun getRecentlyCompletedMatches(hoursAgo: Int = 24, limit: Int = 20): Result<List<MatchEntity>>
    suspend fun getUpcomingMatches(tournamentId: String? = null, limit: Int? = null): Result<List<MatchEntity>>
    suspend fun getMatchStatistics(matchId: String): Result<List<MatchStatisticsEntity>>
}

/**
 * Tennis Repository Implementation.
 */
class TennisRepositoryImpl(
    private val tennisApi: TennisApi
) : TennisRepository {

    override suspend fun getPlayers(slateId: String?): Result<List<TennisPlayerEntity>> {
        return when (val response = tennisApi.getPlayers(slateId)) {
            is ApiResponse.Success -> {
                Result.success(response.data.map { it.toEntity() })
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getPlayer(playerId: String): Result<TennisPlayerEntity> {
        return when (val response = tennisApi.getPlayer(playerId)) {
            is ApiResponse.Success -> {
                Result.success(response.data.toEntity())
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getLiveMatches(): Result<List<MatchEntity>> {
        return when (val response = tennisApi.getLiveMatches()) {
            is ApiResponse.Success -> {
                Result.success(response.data.map { it.toMatchEntity() })
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getRecentlyCompletedMatches(hoursAgo: Int, limit: Int): Result<List<MatchEntity>> {
        return when (val response = tennisApi.getRecentlyCompletedMatches(hoursAgo, limit)) {
            is ApiResponse.Success -> {
                Result.success(response.data.map { it.toMatchEntity() })
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getUpcomingMatches(tournamentId: String?, limit: Int?): Result<List<MatchEntity>> {
        return when (val response = tennisApi.getUpcomingMatches(tournamentId, limit)) {
            is ApiResponse.Success -> {
                Result.success(response.data.map { it.toMatchEntity() })
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getMatchStatistics(matchId: String): Result<List<MatchStatisticsEntity>> {
        return when (val response = tennisApi.getMatchStatistics(matchId)) {
            is ApiResponse.Success -> {
                Result.success(response.data.map { it.toEntity() })
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }
}

// ===== DTO TO ENTITY MAPPERS =====

private fun TennisPlayerDto.toEntity(): TennisPlayerEntity {
    return TennisPlayerEntity(
        id = id,
        externalId = externalId,
        name = name,
        firstName = firstName,
        lastName = lastName,
        country = country,
        atpRanking = atpRanking,
        eloRating = eloRating ?: 1500.0,
        gender = gender,
        costs = PlayerCosts(
            serve = costs.serve,
            forehand = costs.forehand,
            backhand = costs.backhand,
            volleys = costs.volleys,
            fighting = costs.fighting
        ),
        isActive = isActive,
        avatarUrl = avatarUrl
    )
}

private fun LiveMatchDto.toMatchEntity(): MatchEntity {
    return MatchEntity(
        id = id,
        externalId = externalId,
        tournamentId = tournamentId,
        player1Id = player1Id ?: "",
        player2Id = player2Id ?: "",
        player1Name = player1Name,
        player2Name = player2Name,
        winnerId = winnerId,
        status = status,
        round = round,
        score = if (homeScore != null && awayScore != null) "$homeScore-$awayScore" else null,
        setsPlayed = setsPlayed,
        scheduledTime = scheduledTime,
        startedAt = null,
        completedAt = null,
        court = null,
        competition = competition,
        periodScores = periodScores.mapIndexed { index, it ->
            PeriodScoreEntity(it.homeScore, it.awayScore, it.setNumber ?: (index + 1))
        }
    )
}

private fun MatchStatisticsDto.toEntity(): MatchStatisticsEntity {
    return MatchStatisticsEntity(
        id = id,
        matchId = matchId,
        playerId = playerId,
        aces = aces,
        doubleFaults = doubleFaults,
        firstServePct = firstServePct,
        servePoints = servePoints,
        servePointsWon = servePointsWon,
        forehandWinners = forehandWinners,
        forehandErrors = forehandErrors,
        backhandWinners = backhandWinners,
        backhandErrors = backhandErrors,
        volleyWinners = volleyWinners,
        totalWinners = totalWinners,
        totalUnforcedErrors = totalUnforcedErrors,
        breakPointsConverted = breakPointsConverted,
        breakPointsSaved = breakPointsSaved,
        breakPointsFaced = breakPointsFaced,
        setsPlayed = setsPlayed,
        totalPointsPlayed = totalPointsPlayed,
        isWinner = isWinner
    )
}
